import { useEffect, useRef, useState, type KeyboardEvent, type ReactElement } from 'react';
import type { RoleAssignment, RoleAssignmentsResponse } from '@citibox-panel/api-client';
import {
  AddIcon,
  Alert,
  Box,
  Button,
  EmptyState,
  SearchIcon,
  Snackbar,
  Stack,
  Text,
  TextField,
  type SnackbarNotice,
} from '@citibox-panel/ui';
import { isKnownPermission } from '@/lib/access';
import type { SaveRequest } from '../hooks/useSaveRoleAssignment';
import { isReadOnly } from '../lib/assignments';
import { permissionLabel } from '../lib/catalog';
import { canonicalEmail, extractEmail, isCitiboxEmail } from '../lib/email';
import { AssignmentDialog } from './AssignmentDialog';
import { AssignmentsTable, type AssignmentTableRow } from './AssignmentsTable';

// TODO: i18n
const COPY = {
  title: 'Roles y permisos',
  subtitle: (market: string | null) =>
    market === null
      ? 'Asignaciones del panel. No se puede confirmar el entorno.'
      : `Asignaciones del panel en este entorno (${market})`,
  add: 'Añadir persona',
  filter: 'Filtrar por email',
  loading: 'Cargando asignaciones…',
  loadFailed: 'No se han podido cargar las asignaciones.',
  retry: 'Reintentar',
  noMatch: (filter: string) => `Ningún email coincide con «${filter}»`,
  addCandidate: (email: string) => `Añadir ${email}`,
  nobodyElse: 'Nadie más tiene asignaciones',
  saved: (email: string) => `Cambios guardados: ${email}`,
} as const;

// A save confirmation clears itself after this long, as success notices do elsewhere in the panel;
// the «X» closes it sooner. Errors never leave on a timer (ADR 0021), and they stay in the dialog.
const SAVED_NOTICE_MS = 5000;

export interface RolesManagementPageProps {
  data: RoleAssignmentsResponse | undefined;
  status: 'loading' | 'error' | 'ready';
  onRetry: () => void;
  retrying: boolean;
  selfEmail: string | null;
  marketLabel: string | null;
  save: (request: SaveRequest) => Promise<void>;
  pending: boolean;
  onReloadCurrent: (email: string) => Promise<RoleAssignment | null>;
}

interface DialogOpening {
  key: number;
  mode: 'add' | 'edit';
  initial: RoleAssignment | null;
  initialEmail: string;
}

// Where the keyboard should end up after a save: the row's ✎ when the save kept or put the person
// in the list, the filter when «Quitar todo» took them out. It moves only once the dialog has
// exited, because until then MUI keeps the page aria-hidden.
interface FocusAfterSave {
  email: string;
  listed: boolean;
  exited: boolean;
}

function toRow(assignment: RoleAssignment, catalog: RoleAssignmentsResponse): AssignmentTableRow {
  return {
    assignment,
    readOnly: isReadOnly(assignment, catalog.roles),
    roleChips: assignment.roles.map((role) => {
      const known = catalog.roles.some((candidate) => candidate.id === role.id);
      return {
        key: role.id,
        // A blank display name is tolerated by the API; the id is what the person can recognise.
        label: role.name.trim() === '' ? role.id : role.name,
        unknown: !known,
      };
    }),
    extraChips: assignment.extraPermissions.map((id) => ({
      key: id,
      label: permissionLabel(id),
      unknown: !isKnownPermission(id),
    })),
  };
}

// The «Roles y permisos» screen below its header: the filter is the way in (a chat request brings
// one email), the table is the list, and every change goes through one dialog.
export function RolesManagementPage({
  data,
  status,
  onRetry,
  retrying,
  selfEmail,
  marketLabel,
  save,
  pending,
  onReloadCurrent,
}: RolesManagementPageProps): ReactElement {
  const [filter, setFilter] = useState('');
  const [opening, setOpening] = useState<DialogOpening | null>(null);
  const [open, setOpen] = useState(false);
  const [notices, setNotices] = useState<SnackbarNotice[]>([]);
  const noticeTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  // Leaving the screen drops the pending timers with it.
  useEffect(() => {
    const timers = noticeTimers.current;
    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
    };
  }, []);
  const [saves, setSaves] = useState(0);
  const [exits, setExits] = useState(0);
  const openings = useRef(0);
  const lastRequest = useRef<SaveRequest | null>(null);
  const focusAfterSave = useRef<FocusAfterSave | null>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const editButtons = useRef(new Map<string, HTMLButtonElement | HTMLAnchorElement>());

  // Focus waits for both the dialog's exit and the list on screen agreeing with the save. Moving it
  // while the page is aria-hidden loses it to a screen reader; moving it to a row that is about to
  // vanish (or has not appeared yet) drops the keyboard on <body>.
  useEffect(() => {
    const target = focusAfterSave.current;
    if (target === null || !target.exited || data === undefined) return;
    const listed = data.assignments.some((assignment) => assignment.email === target.email);
    if (listed !== target.listed) return;
    focusAfterSave.current = null;
    const next = editButtons.current.get(target.email) ?? filterRef.current;
    if (next === null) return;
    // A dialog opened from this ✎ already had MUI put focus back on it, but while the page was
    // still hidden. Focusing it again would fire nothing, so it is let go and taken again: the
    // screen reader hears it now that the page is back.
    if (document.activeElement === next) next.blur();
    next.focus();
  }, [data, exits]);

  const rows = data === undefined ? [] : data.assignments.map((assignment) => toRow(assignment, data));
  const needle = canonicalEmail(extractEmail(filter));
  // A full email targets that exact person only: «ana@…» must never surface, or open on Enter,
  // «juana@…». A partial text still filters by substring.
  const fullEmail = isCitiboxEmail(needle);
  const visible =
    needle === ''
      ? rows
      : rows.filter((row) =>
          fullEmail ? row.assignment.email === needle : row.assignment.email.includes(needle),
        );
  const candidate = fullEmail && visible.length === 0 ? needle : null;
  const self = selfEmail === null ? null : canonicalEmail(selfEmail);
  const nobodyElse = rows.every((row) => row.assignment.email === self);
  const existingEmails = new Set(rows.map((row) => row.assignment.email));
  const protectedEmails = new Set(rows.filter((row) => row.readOnly).map((row) => row.assignment.email));

  // A fresh dialog per opening (keyed), so no mark or error from the last one carries over.
  const openDialog = (mode: 'add' | 'edit', initial: RoleAssignment | null, initialEmail: string): void => {
    openings.current += 1;
    setOpening({ key: openings.current, mode, initial, initialEmail });
    setOpen(true);
  };

  const openEdit = (email: string): void => {
    const assignment = data?.assignments.find((candidateRow) => candidateRow.email === email);
    if (assignment !== undefined) openDialog('edit', assignment, '');
  };

  const onFilterKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') return;
    const [only, ...others] = visible;
    if (only === undefined || others.length > 0 || only.readOnly) return;
    openEdit(only.assignment.email);
  };

  const registerEditButton = (
    email: string,
    element: HTMLButtonElement | HTMLAnchorElement | null,
  ): void => {
    if (element === null) editButtons.current.delete(email);
    else editButtons.current.set(email, element);
  };

  const saveRemembering = (request: SaveRequest): Promise<void> => {
    lastRequest.current = request;
    return save(request);
  };

  const dismissNotice = (id: string): void => {
    clearTimeout(noticeTimers.current.get(id));
    noticeTimers.current.delete(id);
    setNotices((current) => current.filter((notice) => notice.id !== id));
  };

  const handleSaved = (email: string): void => {
    setOpen(false);
    const id = `saved-${String(saves + 1)}`;
    setNotices((current) => [...current, { id, severity: 'success', message: COPY.saved(email) }]);
    noticeTimers.current.set(
      id,
      setTimeout(() => {
        dismissNotice(id);
      }, SAVED_NOTICE_MS),
    );
    focusAfterSave.current = {
      email,
      listed: lastRequest.current?.kind !== 'remove-all',
      exited: false,
    };
    setSaves((count) => count + 1);
  };

  // A dialog closed without a save leaves no target: MUI's own focus restore is right then.
  const handleDialogExited = (): void => {
    const target = focusAfterSave.current;
    if (target === null) return;
    focusAfterSave.current = { ...target, exited: true };
    setExits((count) => count + 1);
  };

  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Text variant="h5" component="h1" bold>
            {COPY.title}
          </Text>
          <Text variant="body2" color="muted">
            {COPY.subtitle(marketLabel)}
          </Text>
        </Box>
        <Button
          variant="contained"
          sentenceCase
          startIcon={<AddIcon />}
          disabled={data === undefined}
          onClick={() => {
            openDialog('add', null, '');
          }}
        >
          {COPY.add}
        </Button>
      </Box>

      {status === 'loading' && (
        <Box role="status">
          <Text>{COPY.loading}</Text>
        </Box>
      )}

      {status === 'error' && (
        <Alert
          severity="error"
          action={
            <Button sentenceCase loading={retrying} onClick={onRetry}>
              {COPY.retry}
            </Button>
          }
        >
          {COPY.loadFailed}
        </Alert>
      )}

      {status === 'ready' && (
        <>
          <TextField
            ref={filterRef}
            value={filter}
            onChange={setFilter}
            onKeyDown={onFilterKey}
            variant="outlined"
            aria-label={COPY.filter}
            placeholder={COPY.filter}
            startAdornment={<SearchIcon />}
            autoFocus
          />
          {visible.length > 0 && (
            <AssignmentsTable
              rows={visible}
              selfEmail={selfEmail}
              onEdit={openEdit}
              editButtonRef={registerEditButton}
            />
          )}
          {needle !== '' && visible.length === 0 && (
            <EmptyState
              title={COPY.noMatch(filter.trim())}
              {...(candidate !== null
                ? {
                    action: (
                      <Button
                        variant="outlined"
                        sentenceCase
                        startIcon={<AddIcon />}
                        onClick={() => {
                          openDialog('add', null, candidate);
                        }}
                      >
                        {COPY.addCandidate(candidate)}
                      </Button>
                    ),
                  }
                : {})}
            />
          )}
          {needle === '' && nobodyElse && <EmptyState title={COPY.nobodyElse} />}
        </>
      )}

      {opening !== null && data !== undefined && (
        <AssignmentDialog
          key={opening.key}
          open={open}
          mode={opening.mode}
          initial={opening.initial}
          initialEmail={opening.initialEmail}
          catalog={data}
          marketLabel={marketLabel}
          existingEmails={existingEmails}
          protectedEmails={protectedEmails}
          selfEmail={selfEmail}
          save={saveRemembering}
          pending={pending}
          onReloadCurrent={onReloadCurrent}
          onClose={() => {
            setOpen(false);
          }}
          onSaved={handleSaved}
          onSwitchToEdit={openEdit}
          onExited={handleDialogExited}
        />
      )}

      {/* The slice's own success notice: this surface has no floating error host (ADR 0021), and
          write errors stay inside the dialog. */}
      <Snackbar notices={notices} onDismiss={dismissNotice} />
    </Stack>
  );
}
